import { type Horizon, anchorOf, endOf, shift } from "@beresta/core";
import { type MessageKey, t } from "./i18n.js";

/**
 * Підпис горизонту для людини. Ключ `2026-W35` придатний для адреси
 * й для бази, але в інтерфейсі він нічого не означає.
 *
 * Місяць у родовому відмінку («24 серпня»), бо так пишуть дату українською.
 * Для року й місяця це виглядало б дивно, тому там формати різні —
 * шар локалізації для того й потрібен.
 */
export function horizonLabel(h: Horizon): string {
  const d = anchorOf(h);
  const month = t(`month.${d.month}` as MessageKey);
  switch (h.kind) {
    case "year":
      return t("horizon.year", { year: d.year });
    case "month":
      // Називний, а не родовий: місяць названий сам, без дати.
      return t("horizon.month", { month: t(`monthName.${d.month}` as MessageKey), year: d.year });
    case "week": {
      // `weekOfYear`/`yearOfWeek` у Temporal необовʼязкові (не всі календарі
      // їх мають). Ключ горизонту вже містить обидва числа й перевірений.
      const [year, week] = h.key.split("-W");
      return t("horizon.week", { n: Number(week), year: Number(year) });
    }
    case "day":
      return t("horizon.day", { day: d.day, month, year: d.year });
  }
}

/**
 * Заголовок сторінки горизонту.
 *
 * Від `horizonLabel` відрізняється одним: у дня немає року. Повний підпис
 * лишається там, де горизонт названий **поза** своїм ланцюгом, — у стрічці
 * й у вікні перенесення, де сусідньої крихти «2026» немає й рік нести
 * нічому.
 */
export function headingLabel(h: Horizon): string {
  if (h.kind !== "day") return horizonLabel(h);
  const d = anchorOf(h);
  return t("horizon.dayHead", { day: d.day, month: t(`month.${d.month}` as MessageKey) });
}

/**
 * Короткий підпис для хлібних крихт. Повний туди не вміщається: «52-й
 * тиждень 2026» поруч із «Грудень 2026» на 390 px переносить рядок, а
 * крихти мусять лишатися однорядковими — інакше вони перестають читатися
 * як шлях і починають виглядати як список.
 */
export function trailLabel(h: Horizon): string {
  const d = anchorOf(h);
  switch (h.kind) {
    case "year":
      return String(d.year);
    case "month":
      return t(`monthName.${d.month}` as MessageKey);
    case "week":
      return t("trail.week", { n: Number(h.key.split("-W")[1]) });
    case "day":
      // Не саме число: у бічній панелі «4» поруч із «Березень» і «Тиж 10»
      // не означає нічого. У крихтах цей випадок не трапляється — поточний
      // масштаб у них не показується.
      return t("trail.day", { day: d.day, month: t(`month.${d.month}` as MessageKey) });
  }
}

/** Назва дня тижня повністю — заголовок дня починається з неї. */
export function weekdayLabel(h: Horizon): string {
  return t(`weekdayFull.${anchorOf(h).dayOfWeek}` as MessageKey);
}

/**
 * Підпис над датою в шапці «Часу» (картки ScreenDay, ScreenWeek,
 * ScreenMonth, ScreenYear): день тижня, «Тиждень 39», рік місяця, «Рік».
 * Великими літерами його робить клас `label`, а не текст.
 */
export function kickLabel(h: Horizon): string {
  const d = anchorOf(h);
  switch (h.kind) {
    case "year":
      return t("time.kick.year");
    case "month":
      return String(d.year);
    case "week":
      return t("time.kick.week", { n: Number(h.key.split("-W")[1]) });
    case "day":
      return weekdayLabel(h);
  }
}

/**
 * Заголовок масштабу на смузі: «21 вересня», «21–27 вересня»,
 * «Вересень», «2026». Тиждень через межу місяця — «28 вересня – 4 жовтня».
 */
export function bandTitle(h: Horizon): string {
  const d = anchorOf(h);
  switch (h.kind) {
    case "year":
      return String(d.year);
    case "month":
      return t(`monthName.${d.month}` as MessageKey);
    case "week": {
      const end = endOf(h);
      if (end.month === d.month)
        return t("time.week.range", { from: d.day, to: end.day, month: t(`month.${d.month}` as MessageKey) });
      return t("time.week.cross", {
        from: d.day,
        fromMonth: t(`month.${d.month}` as MessageKey),
        to: end.day,
        toMonth: t(`month.${end.month}` as MessageKey),
      });
    }
    case "day":
      return headingLabel(h);
  }
}

/**
 * Напис наступного періоду праворуч від заголовка: «Завтра», «18 вер.»,
 * «Тиж. 40», «Жовтень», «2027». Короткий навмисно — на 320 він має
 * вміститися поруч із датою повністю.
 */
export function nextLabel(h: Horizon, todayKey: string): string {
  const d = anchorOf(h);
  switch (h.kind) {
    case "year":
      return String(d.year);
    case "month":
      return t(`monthName.${d.month}` as MessageKey);
    case "week":
      return t("time.next.week", { n: Number(h.key.split("-W")[1]) });
    case "day":
      if (shift({ kind: "day", key: todayKey }, 1).key === h.key) return t("time.tomorrow");
      if (shift({ kind: "day", key: todayKey }, -1).key === h.key) return t("time.yesterday");
      return t("time.next.day", { day: d.day, month: t(`monthShort.${d.month}` as MessageKey) });
  }
}
